import {
	JobEntityType,
	PatientEntityType,
	SupabaseShareEntityViewEnum,
	SupabaseTableEnum,
} from "@/lib/supabase/supabaseTypes";
import { StateCreator } from "zustand";
import { useCentralStore } from "../Central";
import { Logger } from "@/lib/logger/Logger";
import { supabase } from "@/lib/supabase";
import { createSecurePatientName } from "../../lib/utils/utils-functions";
import { showNotification } from "../Central/selectors";
import { PatientSearchResult, PatientSearchMultiResult } from "../Search/types";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";
import { getLabCountry } from "../../lib/utils/country-utils";

const PINNED_PATIENTS_KEY = "dentlab-pinned-patients";

/* Checks if the patient is a PatientEntityType, i.e. has all information
 * as opposed to LazyPatient, which only fetches the minimum information lazily.
 */
export function isLazyPatient(
	patient: LazyPatient | PatientEntityType
): patient is LazyPatient {
	return (patient as LazyPatient).patient_id !== undefined;
}

export function isPatientEntityType(
	patient: LazyPatient | PatientEntityType
): patient is PatientEntityType {
	return !isLazyPatient(patient);
}

function getLazyPatientLookup(
	data: JobsWithPatientResult[] | PatientSearchResult[]
): Record<string, LazyPatient> {
	return (data as Array<JobsWithPatientResult | PatientSearchResult>).reduce(
		(
			acc: Record<string, LazyPatient>,
			patient: JobsWithPatientResult | PatientSearchResult
		) => {
			if (!patient) {
				return acc;
			}
			if ("patient_id" in patient && patient.patient_id) {
				// Case where the patient is a LazyPatient
				acc[patient.patient_id] = {
					patient_id: patient.patient_id,
					first_name: patient.first_name ?? "",
					last_name: patient.last_name ?? "",
					code: patient?.code ?? "",
					title: patient?.title ?? "",
					client_id: patient.client_id ?? undefined,
				};
			} else if ("id" in patient && patient.id) {
				// Case where the patient is a PatientSearchResult
				acc[patient.id] = {
					patient_id: patient.id,
					first_name: patient?.first_name ?? "",
					last_name: patient?.last_name ?? "",
					code: patient?.code ?? "",
					title: patient?.title ?? "",
					client_id: (patient as any).client_id ?? undefined,
				};
			}
			return acc;
		},
		{} as Record<string, LazyPatient>
	);
}

interface JobsWithPatientResult {
	client_id: string | null;
	code: string | null;
	first_name: string | null;
	last_name: string | null;
	patient_id: string | null;
	title: string | null;
}

export interface LazyPatient {
	patient_id: string;
	first_name: string;
	last_name: string;
	code: string;
	title: string;
	client_id?: string;
}

/**
 * - fetchPatients is called whenever a client is opened for all patients of that client to improve performance.
 * - getPatient is called whenever a patient is opened to fetch the patient data.
 */
export interface PatientStoreSlice {
	patientsLookup: Record<string, LazyPatient | PatientEntityType>;
	fetchedClientsCache: Record<string, boolean>;
	pinnedPatients: Record<string, PatientEntityType>;
	fetchPatients: (clientId: string) => Promise<void>;
	fetchPatientSearchResults: (
		query: string,
		clientId?: string | null
	) => Promise<LazyPatient[] | null>;
	fetchPatientSearchResultsMulti: (params: {
		first_name?: string | null;
		last_name?: string | null;
		code?: string | null;
		birth_date?: string | null;
		priority_field?:
			| "first_name"
			| "last_name"
			| "code"
			| "birth_date"
			| null;
		clientId?: string | null;
	}) => Promise<PatientSearchMultiResult[] | null>;
	fetchPatientsByIds: (patientIds: string[]) => Promise<void>;
	fetchOrgPatientsForDuplicates: (
		sourcePatientId: string
	) => Promise<PatientEntityType[]>;
	getPatient: (patientId: string) => Promise<PatientEntityType | null>;
	upsertPatient: (
		patient: PatientEntityType,
		saveToDb?: boolean
	) => Promise<void>;
	deletePatient: (patientId: string) => Promise<boolean>;
	mergePatients: (keeperId: string, mergeIds: string[]) => Promise<boolean>;
	fetchPatientJobsMap: (
		patientIds: string[]
	) => Promise<Record<string, JobEntityType[]>>;
	transferJobToPatient: (
		jobId: number,
		targetPatientId: string
	) => Promise<boolean>;
	removePatientFromLookup: (patientId: string) => void;
	patientName: (
		patient: LazyPatient | PatientEntityType,
		orderLastNameFirst?: boolean
	) => string;
	createPatientFromName: (name: string) => Promise<PatientEntityType | null>;
	insertQuickPatient: (
		fields: Partial<PatientEntityType>,
		clientId?: string | null
	) => Promise<PatientEntityType | null>;
	pinPatient: (patient: PatientEntityType) => void;
	unpinPatient: (patientId: string) => void;
	unpinAllPatients: () => void;
}

export const createPatientStore: StateCreator<PatientStoreSlice> = (
	set,
	get
) => ({
	patientsLookup: {},
	fetchedClientsCache: {},
	pinnedPatients: getLocalStorage<Record<string, PatientEntityType>>(
		PINNED_PATIENTS_KEY,
		{}
	),
	fetchPatients: async (clientId: string) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("No organization ID");
			return;
		}

		if (get().fetchedClientsCache[clientId]) {
			return;
		}

		let allData: JobsWithPatientResult[] = [];
		let hasMore = true;
		let page = 0;
		const pageSize = 1000;

		while (hasMore) {
			const { data, error } = await supabase
				.from(SupabaseShareEntityViewEnum.JOBS_WITH_PATIENT)
				.select()
				.eq("client_id", clientId)
				.range(page * pageSize, (page + 1) * pageSize - 1);

			if (error) {
				Logger.error(error);
				return;
			}

			if (data) {
				allData = [...allData, ...data];
				hasMore = data.length === pageSize;
				page++;
			} else {
				hasMore = false;
			}
		}

		// Merge the lazy patients lookup with the existing patients lookup; where the existing patients lookup takes precedence
		// because it has more data
		set((state) => ({
			patientsLookup: {
				...getLazyPatientLookup(allData),
				...state.patientsLookup,
			},
		}));
		set((state) => ({
			fetchedClientsCache: {
				...state.fetchedClientsCache,
				[clientId]: true,
			},
		}));
	},
	fetchPatientSearchResults: async (
		query: string,
		clientId?: string | null
	) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			return null;
		}
		const { data, error } = await supabase.rpc("search_patients", {
			query: query,
			p_organization_id: organizationId,
			p_client_id: clientId || undefined,
		});

		if (error) {
			Logger.error(error);
			return null;
		}
		const lazyPatientsLookup = getLazyPatientLookup(data);
		set((state) => ({
			patientsLookup: {
				...lazyPatientsLookup,
				...state.patientsLookup,
			},
		}));

		return Object.values(lazyPatientsLookup);
	},
	fetchPatientSearchResultsMulti: async ({
		first_name,
		last_name,
		code,
		birth_date,
		priority_field,
		clientId,
	}) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			return null;
		}
		const hasAny =
			!!first_name?.trim() ||
			!!last_name?.trim() ||
			!!code?.trim() ||
			!!birth_date?.trim();
		if (!hasAny) {
			return [];
		}
		const { data, error } = await supabase.rpc("search_patients_multi", {
			p_organization_id: organizationId,
			p_first_name: first_name?.trim() || undefined,
			p_last_name: last_name?.trim() || undefined,
			p_code: code?.trim() || undefined,
			p_birth_date: birth_date?.trim() || undefined,
			p_priority_field: priority_field ?? undefined,
			p_client_id: clientId ?? undefined,
			p_limit: 50,
		});
		if (error) {
			Logger.error(error);
			return null;
		}
		return (data ?? []) as PatientSearchMultiResult[];
	},
	fetchPatientsByIds: async (patientIds: string[]) => {
		if (patientIds.length === 0) {
			return;
		} else if (patientIds.length > 1000) {
			showNotification({
				message: "Es wurden mehr als 1000 Patienten ausgewählt.",
				type: "warning",
			});
			return;
		}

		const { data, error } = await supabase
			.from("patients")
			.select()
			.in("id", patientIds);

		if (error) {
			Logger.error(error);
			return;
		}

		const newPatientsLookup = data.reduce(
			(acc, patient) => {
				if (patient.id) {
					acc[patient.id] = patient;
				}
				return acc;
			},
			{} as Record<string, PatientEntityType>
		);

		set((state) => ({
			patientsLookup: {
				...state.patientsLookup,
				...newPatientsLookup,
			},
		}));
	},
	fetchPatientsForMonthlyInvoices: async (clientId: string) => {
		await get().fetchPatients(clientId);
	},
	fetchOrgPatientsForDuplicates: async (sourcePatientId) => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId || !sourcePatientId) {
			return [];
		}

		// Scan every patient in the org, once. The caller re-ranks in memory
		// whenever the strictness changes — no prefix anchor (which would hide
		// front-of-name typos) and no DB round-trip per slider step.
		const candidates: PatientEntityType[] = [];
		const pageSize = 1000;
		let page = 0;
		let hasMore = true;

		while (hasMore) {
			const { data, error } = await supabase
				.from("patients")
				.select()
				.eq("organization_id", organizationId)
				.neq("id", sourcePatientId)
				.range(page * pageSize, (page + 1) * pageSize - 1);

			if (error) {
				Logger.error(error);
				return [];
			}

			candidates.push(...(data ?? []));
			hasMore = (data?.length ?? 0) === pageSize;
			page++;
		}

		return candidates;
	},
	getPatient: async (patientId: string) => {
		const patientsLookup = get().patientsLookup;
		const existingPatient = patientsLookup[patientId];

		if (existingPatient && !isLazyPatient(existingPatient)) {
			return existingPatient as PatientEntityType;
		} else {
			const { data, error } = await supabase
				.from("patients")
				.select()
				.eq("id", patientId)
				.single();

			if (error) {
				Logger.error(error);
				return null;
			}

			set((state) => ({
				patientsLookup: {
					...state.patientsLookup,
					[patientId]: data,
				},
			}));

			return data;
		}
	},
	upsertPatient: async (patient, saveToDb = false) => {
		if (!patient.id) {
			Logger.error("No patient ID when upserting patient");
			return;
		}

		let newPatient: PatientEntityType;

		if (saveToDb) {
			const { client_id, search_vector, ...patientData } = patient as any;
			const { data, error } = await supabase
				.from(SupabaseTableEnum.PATIENTS)
				.upsert(patientData, {
					onConflict: "id",
				})
				.select()
				.single();

			if (error) {
				Logger.error(error);
				return;
			}

			newPatient = data;
		} else {
			newPatient = patient;
		}

		set((state) => ({
			patientsLookup: {
				...state.patientsLookup,
				[newPatient.id as string]: {
					...newPatient,
				},
			},
		}));
	},
	patientName: (patient, orderLastNameFirst = false) => {
		const isPrivacyOn =
			!!useCentralStore.getState().profile?.data_privacy_mode;

		return createSecurePatientName(
			(patient as PatientEntityType)?.id ??
				(patient as LazyPatient)?.patient_id ??
				null,
			patient?.first_name ?? "",
			patient?.last_name ?? "",
			isPrivacyOn,
			orderLastNameFirst
		);
	},
	insertQuickPatient: async (
		fields: Partial<PatientEntityType>,
		clientId?: string | null
	) => {
		const { organization } = useCentralStore.getState();
		const organizationId = organization?.id;
		if (!organizationId) {
			Logger.error("No organization ID for quick-insert patient");
			return null;
		}
		// Empty strings from form state must become null for nullable columns
		// (especially `birth_date` which is a date column and rejects "").
		const orNull = (v: unknown) =>
			typeof v === "string" && v.trim() === "" ? null : v ?? null;
		const payload: any = {
			organization_id: organizationId,
			code:
				typeof fields.code === "string" && fields.code.trim() !== ""
					? fields.code
					: "",
			first_name: orNull(fields.first_name),
			last_name: orNull(fields.last_name),
			title: orNull(fields.title),
			birth_date: orNull(fields.birth_date),
			gender: orNull(fields.gender),
			street: orNull(fields.street),
			city: orNull(fields.city),
			postal_code: orNull(fields.postal_code),
			country: orNull(fields.country) ?? getLabCountry(organization),
			additional_information: orNull(fields.additional_information),
			ahv_number: orNull(fields.ahv_number),
		};
		if (clientId) {
			payload.client_id = clientId;
		}
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PATIENTS)
			.insert(payload)
			.select()
			.single();
		if (error) {
			Logger.error(error);
			showNotification({
				message: "Patient konnte nicht angelegt werden",
				type: "error",
			});
			return null;
		}
		set((state) => ({
			patientsLookup: {
				...state.patientsLookup,
				[data.id]: data,
			},
		}));
		return data;
	},
	deletePatient: async (patientId: string) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.PATIENTS)
			.delete()
			.eq("id", patientId);
		if (error) {
			Logger.error(error);
			return false;
		}
		set((state) => {
			const { [patientId]: _removed, ...rest } = state.patientsLookup;
			return { patientsLookup: rest };
		});
		return true;
	},
	mergePatients: async (keeperId, mergeIds) => {
		const ids = Array.from(
			new Set(mergeIds.filter((id) => id && id !== keeperId))
		);
		if (ids.length === 0) {
			return false;
		}
		// The surviving patient's Auftraggeber is derived by the RPC from its most
		// recent job after the merge.
		const { error } = await supabase.rpc("merge_patients_bulk", {
			patient_id_stay: keeperId,
			patient_ids_go: ids,
		});
		if (error) {
			Logger.error(error);
			showNotification({
				message: `Fehler beim Zusammenlegen der Patienten: ${error.message}`,
				type: "error",
			});
			return false;
		}
		// The keeper's data changed and every merged patient is hard-deleted; drop
		// them all from the lookup so no view shows a stale row.
		set((state) => {
			const patientsLookup = { ...state.patientsLookup };
			delete patientsLookup[keeperId];
			for (const id of ids) {
				delete patientsLookup[id];
			}
			return { patientsLookup };
		});
		return true;
	},
	fetchPatientJobsMap: async (patientIds) => {
		const ids = patientIds.filter(Boolean);
		if (ids.length === 0) return {};
		const { data, error } = await supabase
			.from("jobs")
			.select()
			.in("patient_id", ids)
			.order("incoming_order_date", { ascending: false });
		if (error) {
			Logger.error(error);
			return {};
		}
		const map: Record<string, JobEntityType[]> = {};
		for (const job of data ?? []) {
			if (!job.patient_id) continue;
			(map[job.patient_id] ??= []).push(job);
		}
		return map;
	},
	// The update_patient_client_id trigger re-derives both patients' client_id
	// from their jobs, so a plain jobs update is all a transfer needs.
	transferJobToPatient: async (jobId, targetPatientId) => {
		const { error } = await supabase
			.from("jobs")
			.update({ patient_id: targetPatientId })
			.eq("id", jobId);
		if (error) {
			Logger.error(error);
			showNotification({
				message: `Auftrag konnte nicht verschoben werden: ${error.message}`,
				type: "error",
			});
			return false;
		}
		showNotification({
			message: "Auftrag wurde zum Patienten verschoben.",
			type: "success",
		});
		return true;
	},
	removePatientFromLookup: (patientId: string) => {
		set((state) => {
			const { [patientId]: _removed, ...rest } = state.patientsLookup;
			return { patientsLookup: rest };
		});
	},
	createPatientFromName: async (name: string) => {
		if (!name) {
			return null;
		}
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			return null;
		}
		const firstName = name?.split(" ")[0];
		const lastName = name?.split(" ")[1];
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PATIENTS)
			.insert({
				first_name: firstName,
				last_name: lastName,
				organization_id: organizationId,
				code: "",
			})
			.select()
			.single();
		if (error) {
			Logger.error(error);
			return null;
		}
		const patientId = data.id;
		if (!patientId) {
			return null;
		}
		set((state) => ({
			patientsLookup: {
				...state.patientsLookup,
				[patientId]: data,
			},
		}));
		return data;
	},
	pinPatient: (patient: PatientEntityType) => {
		if (!patient.id) return;
		set((state) => {
			const newPinnedPatients = {
				...state.pinnedPatients,
				[patient.id as string]: patient,
			};
			setLocalStorage(PINNED_PATIENTS_KEY, newPinnedPatients);
			return { pinnedPatients: newPinnedPatients };
		});
	},
	unpinPatient: (patientId: string) => {
		if (!patientId) return;
		set((state) => {
			const { [patientId]: _removed, ...rest } = state.pinnedPatients;
			setLocalStorage(PINNED_PATIENTS_KEY, rest);
			return { pinnedPatients: rest };
		});
	},
	unpinAllPatients: () => {
		setLocalStorage(PINNED_PATIENTS_KEY, {});
		set({ pinnedPatients: {} });
	},
});
